import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { useCastersContext } from "@/pages/caster/caster-data-entry-v2/CastersContext";
import { useSaveStrand } from "@/pages/caster/caster-data-entry-v2/hooks/useSaveStrand";
import { StrandData } from "@/pages/caster/models/StrandData";
import {
	ButtonWithLoading,
	HelmetDexteel,
	MesfModal,
} from "@dexteel/mesf-core";
import { Button, FormControl, Grid2 as Grid, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LengthInput } from "../../../modals/delivery-ticket/ProductBatch/components/LengthInput";
import { SectionAutocomplete } from "../../../ui/SectionAutocomplete";

interface EditStrandModalProps {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	strandId: number | null;
}

type FormValues = {
	PieceLength: number | null;
	Pieces: number | null;
	SectionId: number | null;
};
export const EditStrandModal = ({
	show,
	onHide,
	strandId,
}: EditStrandModalProps) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
	const [pendingFormData, setPendingFormData] = useState<FormValues | null>(
		null,
	);
	const { state } = useCastersContext();
	const { save: saveStrand, saveAllStrands } = useSaveStrand();
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			PieceLength: 0,
			Pieces: 0,
			SectionId: 0,
		},
	});

	const strandList = state.strands;
	const strand = state.strands?.find((s: StrandData) => s.Strand === strandId);
	const title = strandId ? `Strand ${strandId}` : "All Strands";

	const handleConfirmationDialogClose = (value?: string) => {
		setShowConfirmationDialog(false);
		if (value === "OK" && pendingFormData) {
			handleSaveAllStrands(pendingFormData);
		}
	};

	const handleSaveAllStrands = async (data: FormValues) => {
		setIsSubmitLoading(true);
		await saveAllStrands("PieceLength", data.PieceLength);
		await saveAllStrands("Pieces", data.Pieces);
		await saveAllStrands("SectionId", data.SectionId);
		setIsSubmitLoading(false);
		onHide(true);
	};

	const onSubmit: SubmitHandler<FormValues> = async (data) => {
		if (strandId) {
			setIsSubmitLoading(true);
			await saveStrand(strandId, "PieceLength", data.PieceLength);
			await saveStrand(strandId, "Pieces", data.Pieces || null);
			await saveStrand(strandId, "SectionId", data.SectionId);
			setIsSubmitLoading(false);
			onHide(true);
		} else {
			setPendingFormData(data);
			setShowConfirmationDialog(true);
		}
	};

	useEffect(() => {
		if (show) {
			if (strandId && strand) {
				reset({
					PieceLength: strand.PieceLength,
					Pieces: strand.Pieces,
					SectionId: strand.SectionId,
				});
			}
			if (strandId === null && strandList) {
				const firstStrand = strandList[0];
				reset({
					PieceLength: firstStrand.PieceLength,
					Pieces: firstStrand.Pieces,
					SectionId: firstStrand.SectionId,
				});
			}
		}
	}, [show, reset, strandId]);

	return (
		<>
			{show && <HelmetDexteel title={`Edit ${title}`} />}
			<MesfModal
				open={show}
				handleClose={() => onHide(false)}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
				maxWidth="sm"
				title={`Edit ${title}`}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<Grid container spacing={2} justifyContent="space-between">
							<Grid size={{ xs: 6, md: 4 }}>
								<Controller
									name="PieceLength"
									control={control}
									render={({ field }) => (
										<LengthInput
											feet={Math.floor((field.value || 0) / 12)}
											inches={(field.value || 0) % 12}
											onChange={(feet, inches) =>
												field.onChange(feet * 12 + inches)
											}
											error={!!errors.PieceLength}
											helperText={errors.PieceLength?.message}
										/>
									)}
								/>
							</Grid>
							<Grid size={{ xs: 6, md: 4 }}>
								<Controller
									name="Pieces"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											type="number"
											label="Pieces"
											size="small"
											variant="outlined"
											InputProps={{ inputProps: { min: 0 } }}
											error={!!errors.Pieces}
											helperText={errors.Pieces?.message}
										/>
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 4 }}>
								<FormControl variant="outlined" fullWidth size="small">
									<Controller
										name="SectionId"
										control={control}
										render={({ field }) => (
											<SectionAutocomplete
												value={field.value}
												onChange={field.onChange}
												onFocus={() => {}}
												moldSizeId={field.value}
												label="Section"
											/>
										)}
									/>
								</FormControl>
							</Grid>
						</Grid>
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ xs: 12, md: 6 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>

			<ConfirmationDialogRaw
				title="Confirm Changes"
				message="You are about to apply these changes to all 3 strands. Do you want to proceed?"
				open={showConfirmationDialog}
				onClose={handleConfirmationDialogClose}
			/>
		</>
	);
};
